import { Modal, PregnancyCard } from "@aph/ui-lib-humanx";
import { useRouter } from "next/navigation";
import React, { useState } from "react";
import { FirstFoldPregnancy, HOME_TYPE } from "@/components/PageHome/api/interface";
import BirthFlow from "@/components/PageHome/components/OnboardingSection/Flows/Birth";
import NewMomFlow from "@/components/PageHome/components/OnboardingSection/Flows/NewMom";
import PregnancyFlow from "@/components/PageHome/components/OnboardingSection/Flows/Pregnancy";
import styles from "@/components/PageHome/components/OnboardingSection/Onboarding.module.scss";
import { clearFlowData, setCurrentFlow } from "@/components/PageHome/redux/slice";
import { dateToString, nthNumber } from "@/helpers";
import { ONBOARDING_STATUS, PREGNANCY_CIRCLE_END_VALUE } from "@/helpers/constants";
import routes from "@/helpers/router";
import { buttonClickedEvent } from "@/helpers/TSDEvents";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { SignedinState } from "@/redux/slice/auth/enums/SignedInState";
import signedInStateSelector from "@/redux/slice/auth/selectors/signedInState";

interface PregnancyProps {
  data: FirstFoldPregnancy;
  type: `${HOME_TYPE}`;
}

const FIRST_FOLD = {
  [HOME_TYPE.PREGNANCY]: {
    title: "Congrats on your pregnancy!",
    subTitle: "Share your due date to help us create a personalised guided journey for you.",
    ctaText: "Read about pregnancy",
  },
  [HOME_TYPE.BIRTH]: {
    title: "Exciting times ahead!",
    subTitle: "Share your due date, and let us guide you towards stress-free delivery experience!",
    ctaText: "Read about labour",
  },
};

const Pregnancy: React.FC<PregnancyProps> = ({ data, type }) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [modalType, setModalType] = useState<HOME_TYPE | null>(null);

  const router = useRouter();

  const dispatch = useAppDispatch();
  const signedInStatus = useAppSelector(signedInStateSelector);

  const dueDate = data?.dueDate ? new Date(data.dueDate) : undefined;
  const currentTrimester = data?.currentTrimester;
  const currentWeek = data?.currentWeek;
  const imageURL = data?.firstFoldImage;

  const openModal: () => void = () => {
    handleClevertapTracking({ buttonName: dueDate ? "Edit due date" : "Add due date" });
    dispatch(clearFlowData());
    dispatch(setCurrentFlow(type));
    setIsModalOpen(true);
  };

  const handleClevertapTracking = (props: { buttonName: string }) => {
    const { buttonName } = props;
    buttonClickedEvent({
      button_name: buttonName,
      momverse_stage: ONBOARDING_STATUS[type],
      is_loggedin: signedInStatus === SignedinState.SIGNEDIN ? true : false,
    });
  };

  const pregnancyDate = [
    dueDate
      ? {
          label: `Due date: ${dateToString(dueDate, "do MMM''yy")}`,
          onClick: openModal,
          icon: "edit",
        }
      : {
          label: `Add due date`,
          onClick: openModal,
          icon: "add",
        },
  ];

  const getTitle: () => string = () => {
    if (typeof currentWeek === "number" && dueDate) {
      return "Hello Mamma!";
    }

    return FIRST_FOLD[type].title;
  };

  const getSubtitle: () => string = () => {
    if (typeof currentWeek === "number" && dueDate) {
      return `You are now in your ${currentTrimester}${nthNumber(currentTrimester)} trimester and ${currentWeek} weeks pregnant`;
    }

    return FIRST_FOLD[type].subTitle;
  };

  const getCtaText: () => string = () => {
    if (typeof currentWeek === "number" && currentWeek > 40) {
      return `Onboard as a new mother`;
    }

    if (typeof currentWeek === "number" && dueDate) {
      return `Read about week ${currentWeek}`;
    }

    return FIRST_FOLD[type].ctaText;
  };

  const handleCtaClick = () => {
    handleClevertapTracking({ buttonName: getCtaText() });

    if (typeof currentWeek === "number" && currentWeek > 40) {
      setModalType(HOME_TYPE.NEW_MOM);
      dispatch(clearFlowData());
      dispatch(setCurrentFlow(HOME_TYPE.NEW_MOM));
      setIsModalOpen(true);

      return;
    }

    if (typeof currentWeek === "number" && dueDate) {
      router.push(routes.cluster(`pregnancy-week-${currentWeek}`));
    } else {
      if (type === HOME_TYPE.BIRTH) router.push(routes.pillar(`labour-and-delivery`));
      else router.push(routes.category(`pregnancy`));
    }
  };

  const handleModalOpen = (modalStatus) => {
    if (modalStatus === false) {
      setIsModalOpen(false);
      modalType && setModalType(null);
    }
  };

  return (
    <div className={`container spacingBetweenSections ${styles.firstFoldContainer}`}>
      <PregnancyCard
        pregnancyDate={pregnancyDate}
        title={getTitle()}
        subTitle={getSubtitle()}
        insideLoaderImage={imageURL}
        circleEndValue={PREGNANCY_CIRCLE_END_VALUE}
        ctaClick={handleCtaClick}
        circleTargetValue={currentWeek}
        ctaText={getCtaText()}
        isShowProgressLoader={!!dueDate}
      />
      <Modal
        size="md"
        backdropClose
        mobileBottomAlign
        titleAlign="center"
        open={isModalOpen}
        handleOpen={(modalStatus: boolean) => {
          handleModalOpen(modalStatus);
        }}
        isHXModal
        modalClass={styles.modalClass}
        titleClass={styles.titleClass}
        modalboxClass={styles.modalboxClass}
        modalContentClass={styles.innerSection}
      >
        {modalType === HOME_TYPE.NEW_MOM && <NewMomFlow isModalOpen={isModalOpen} setIsModalOpen={handleModalOpen} />}
        {type === HOME_TYPE.PREGNANCY && !modalType && (
          <PregnancyFlow dueDate={dueDate} setIsModalOpen={handleModalOpen} isModalOpen={isModalOpen} />
        )}
        {type === HOME_TYPE.BIRTH && !modalType && (
          <BirthFlow dueDate={dueDate} setIsModalOpen={handleModalOpen} isModalOpen={isModalOpen} />
        )}
      </Modal>
    </div>
  );
};

export default Pregnancy;
